import type {SiteLocale} from "seo/siteMeta";
import {localeCopyWithEnglishFallback} from "i18n/localeFallback";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_DE} from "i18n/de/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_EN} from "i18n/en/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_FR} from "i18n/fr/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_IT} from "i18n/it/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_RO} from "i18n/ro/planLabels";

/**
 * Per-page copy for the public **Peer-to-peer transfer mode** page (`/p2p`, issue ui#133).
 *
 * One of the TWO files a content agent edits to fill in this page (the other is
 * {@link ../P2PContent.tsx}). Holds every locale; the page selects one at runtime via `useLocale()`,
 * so the `/ro` and `/de` SSG trees render their own language for free. Routes, SEO wiring and the
 * Resources link already exist - only the strings below and the content component's markup carry the
 * content.
 *
 * The shape is intentionally richer than the scaffold's `{heading, lede, body}`: the page is a real
 * explainer (intro → titled sections with bullets → related-mode cross-links), so the copy is
 * structured to keep the component a dumb view - it just maps this data to semantic HTML in `<Prose>`.
 *
 * Every factual claim here is grounded in the actual P2P transport, so the page stays honest:
 *  - files stream browser → browser over an `RTCDataChannel` and are never stored on a server
 *    (`core/p2p/PeerTransfer.ts`, `core/transfer/TransferService.ts` `instantUpload`); on a network that
 *    blocks a direct connection the bytes pass through the TURN relay below, which forwards DTLS
 *    ciphertext it cannot read, so the page may say "never stored", never "never touch a server";
 *  - the data channel is DTLS-encrypted end-to-end between the two peers (`PeerTransfer.ts`);
 *  - NAT traversal reaches restrictive networks as far as STUN can punch through, and now falls back
 *    to a TURN relay for the rest. The CLIENT has been TURN-capable since ui#85
 *    (`core/p2p/iceServers.ts` arms the negotiator from `GET /turn/ice-servers`), and devops#31 armed
 *    the relay INFRASTRUCTURE on 2026-08-04, so that endpoint now answers with real Cloudflare TURN
 *    credentials on both deployed environments. Verified live (ui#412): the endpoint returns
 *    credentialed entries in production, and `e2e/deployed-turn-relay.spec.ts` forces
 *    `iceTransportPolicy: "relay"` on two independent browser contexts and observes a data channel
 *    open and exchange bytes through the relay alone. The limitations section below now says a relay
 *    fallback exists. Keep it in sync with this file's OTHER LOCALES if any of it changes: three of
 *    them now, so a claim edited in one language and not the others is three pages disagreeing about
 *    the transport rather than two.
 */

/** A localized cross-link to a related mode page. `to` is an internal route, identical per locale. */
export interface P2PLink {
	/** Internal route target (same string in every locale). */
	to: string;
	/** The localized anchor text. */
	label: string;
	/** One-line explanation of when that mode is the better fit. */
	note: string;
}

/** A single list item in a section - an optional bolded lead-in plus its explanation. */
export interface P2PBullet {
	/** Optional bolded lead-in (e.g. "Speed."). Omit for a plain bullet. */
	term?: string;
	/** The bullet's body text. */
	text: string;
}

/** A titled block of the explainer: a heading, optional paragraphs, and an optional bullet list. */
export interface P2PSection {
	/** The `<h2>` for this block. */
	title: string;
	/** Body paragraphs - each renders as its own `<p>` above the bullets. */
	paragraphs?: string[];
	/** Optional bullet list rendered under the paragraphs. */
	bullets?: P2PBullet[];
}

export interface P2PCopy {
	/** `<Seo>` metadata - the browser/search title and meta description for this page. */
	seo: {
		/** Page title (the site name is appended automatically by `<Seo>`). */
		title: string;
		/** Meta description (~150-160 chars for a healthy search snippet). */
		description: string;
	};
	/** The single `<h1>` at the top of the page. */
	heading: string;
	/** One-line intro paragraph directly under the heading. */
	lede: string;
	/** Framing paragraphs between the lede and the first titled section. */
	intro: string[];
	/** The explainer body: how it works, why choose it, what to know. */
	sections: P2PSection[];
	/** Closing "other ways to send" block that cross-links the hosted + zero-knowledge pages. */
	related: {
		/** The `<h2>` for the related-modes block. */
		title: string;
		/** Lead-in sentence before the links. */
		intro: string;
		/** The related-mode links (hosted transfer, zero-knowledge). */
		items: P2PLink[];
	};
}

/** EN + RO + DE + FR + IT copy, keyed by locale. `P2PContent` reads `p2pCopy[locale]`. */
export const p2pCopy: Record<SiteLocale, P2PCopy> = localeCopyWithEnglishFallback<P2PCopy>({
	en: {
		seo: {
			title: "Peer-to-peer transfer, browser to browser",
			description:
				"Send files straight from your browser to the recipient's over an encrypted WebRTC " +
				"connection - nothing is stored on our servers. Fast, private and direct.",
		},
		heading: "Peer-to-peer transfer",
		lede: "Send a file straight from your browser to the person receiving it - no upload, no storage, no middleman.",
		intro: [
			"Peer-to-peer (P2P) mode opens a direct, encrypted connection between your browser and the " +
				"recipient's, then streams the file across it. The bytes travel device to device and never " +
				"land on a Transferify server, so the transfer is as fast as the link between you - and " +
				"private by design.",
		],
		sections: [
			{
				title: "How a direct transfer works",
				paragraphs: [
					"When you choose an instant transfer, Transferify doesn't upload your file. Instead it " +
						"registers the transfer and helps the two browsers find each other: a lightweight signaling " +
						"step exchanges the connection details, and public STUN servers help each side work out how " +
						"to reach the other across the internet.",
					"Once the browsers are connected, the file streams directly over a WebRTC data channel. That " +
						"channel runs over DTLS, so every byte is encrypted end-to-end between the two peers - the " +
						"connection carries the file, and our servers never see its contents.",
				],
			},
			{
				title: "Why send peer-to-peer",
				bullets: [
					{
						term: "Speed.",
						text: "The file takes the shortest path - straight from one browser to the other - instead of " +
							"being uploaded to a server and downloaded again.",
					},
					{
						term: "Privacy.",
						text: "Because the file is never stored, there's nothing sitting on a server to leak, be " +
							"requested, or be forgotten at deletion time. It exists only on the two devices.",
					},
					{
						term: "No storage limits.",
						text: "No server holds your file, so our storage quotas don't apply. Instant is free up to " +
							`${INSTANT_FREE_SIZE_EN} on the free plan, and any paid plan unlocks any ` +
							"size; past that, the practical limit is your connection and how long you both stay online.",
					},
				],
			},
			{
				title: "Good to know before you send",
				paragraphs: [
					"Peer-to-peer is a live handoff rather than a drop-off, so a couple of things are worth " +
						"planning for:",
				],
				bullets: [
					{
						term: "You both need to be online together.",
						text: "The file streams from your browser in real time, so your tab has to stay open until the " +
							"download finishes - close it and the connection ends. For a send-and-forget link the " +
							"recipient can open later, use hosted transfer instead.",
					},
					{
						term: "Some networks block the direct path.",
						text: "Strict firewalls and certain office or mobile networks can stop two browsers from " +
							"connecting directly. Transferify uses STUN to get around most of these, and falls back to a " +
							"relay (TURN) for the rest, so only the most restrictive networks may still not connect. When " +
							"that happens, hosted transfer is the reliable alternative.",
					},
				],
			},
		],
		related: {
			title: "Other ways to send",
			intro: "Peer-to-peer isn't the only option - pick the mode that fits the moment:",
			items: [
				{
					to: "/client-server",
					label: "Hosted transfer",
					note:
						"stores an encrypted copy so the link works even after you close your tab - best when the " +
						"recipient will download later, or when a direct connection won't form.",
				},
				{
					to: "/zero-knowledge",
					label: "Zero-knowledge encryption",
					note:
						"the full story on how your files stay private, including the key that never reaches our " +
						"servers.",
				},
			],
		},
	},
	ro: {
		seo: {
			title: "Transfer peer-to-peer între browsere",
			description:
				"Trimite fișiere direct din browserul tău în cel al destinatarului printr-o conexiune " +
				"WebRTC criptată. Nimic nu se stochează la noi.",
		},
		heading: "Transfer peer-to-peer",
		lede: "Trimite un fișier direct din browserul tău către persoana care îl primește - fără încărcare, fără stocare, fără intermediar.",
		intro: [
			"Modul peer-to-peer (P2P) deschide o conexiune directă și criptată între browserul tău și cel al " +
				"destinatarului, apoi transmite fișierul prin ea. Datele circulă de la un dispozitiv la altul și " +
				"nu sunt stocate niciodată pe un server Transferify, așa că transferul este la fel de rapid ca legătura " +
				"dintre voi - și privat prin însăși natura lui.",
		],
		sections: [
			{
				title: "Cum funcționează un transfer direct",
				paragraphs: [
					"Când alegi un transfer instant, Transferify nu îți încarcă fișierul. În schimb, înregistrează " +
						"transferul și ajută cele două browsere să se găsească: un pas ușor de semnalizare face schimb " +
						"de detalii de conexiune, iar servere STUN publice ajută fiecare parte să afle cum să ajungă la " +
						"cealaltă prin internet.",
					"După ce browserele sunt conectate, fișierul se transmite direct printr-un canal de date WebRTC. " +
						"Acest canal rulează peste DTLS, așa că fiecare octet este criptat end-to-end între cele două " +
						"noduri - conexiunea transportă fișierul, iar serverele noastre nu îi văd niciodată conținutul.",
				],
			},
			{
				title: "De ce să trimiți peer-to-peer",
				bullets: [
					{
						term: "Viteză.",
						text: "Fișierul urmează calea cea mai scurtă - direct de la un browser la celălalt - în loc să " +
							"fie încărcat pe un server și descărcat din nou.",
					},
					{
						term: "Confidențialitate.",
						text: "Pentru că fișierul nu este niciodată stocat, nu există nimic pe un server care să poată fi " +
							"expus, solicitat sau uitat la ștergere. Există doar pe cele două dispozitive.",
					},
					{
						term: "Fără limite de stocare.",
						text: "Niciun server nu îți păstrează fișierul, deci cotele noastre de stocare nu se aplică. " +
							`Pe planul gratuit, transferul instant e gratuit până la ${INSTANT_FREE_SIZE_RO}; peste, orice plan ` +
							`plătit deblochează orice dimensiune. De acolo, limita practică ` +
							"este conexiunea voastră și cât timp rămâneți amândoi online.",
					},
				],
			},
			{
				title: "De reținut înainte să trimiți",
				paragraphs: [
					"Transferul peer-to-peer este o predare în direct, nu o depunere pentru mai târziu, așa că " +
						"merită să ții cont de câteva lucruri:",
				],
				bullets: [
					{
						term: "Trebuie să fiți online în același timp.",
						text: "Fișierul se transmite din browserul tău în timp real, deci fila trebuie să rămână deschisă " +
							"până se termină descărcarea - dacă o închizi, conexiunea se întrerupe. Pentru un link de tip " +
							"trimite-și-uită pe care destinatarul îl poate deschide mai târziu, folosește transferul găzduit.",
					},
					{
						term: "Unele rețele blochează calea directă.",
						text: "Firewall-urile stricte și anumite rețele de birou sau mobile pot împiedica două browsere să " +
							"se conecteze direct. Transferify folosește STUN pentru a depăși majoritatea acestor situații " +
							"și recurge la o soluție de releu (TURN) pentru restul, așa că doar rețelele cele mai " +
							"restrictive s-ar putea să nu se conecteze. În aceste cazuri, transferul găzduit este " +
							"alternativa de încredere.",
					},
				],
			},
		],
		related: {
			title: "Alte moduri de a trimite",
			intro: "Peer-to-peer nu este singura opțiune - alege modul potrivit pentru momentul tău:",
			items: [
				{
					to: "/client-server",
					label: "Transfer găzduit",
					note:
						"stochează o copie criptată, astfel încât linkul funcționează chiar și după ce închizi fila - " +
						"ideal când destinatarul va descărca mai târziu sau când o conexiune directă nu se poate stabili.",
				},
				{
					to: "/zero-knowledge",
					label: "Criptare zero-knowledge",
					note:
						"povestea completă despre cum îți rămân fișierele private, inclusiv cheia care nu ajunge " +
						"niciodată pe serverele noastre.",
				},
			],
		},
	},
	de: {
		seo: {
			title: "Peer-to-Peer-Transfer, Browser zu Browser",
			description:
				"Sende Dateien direkt aus deinem Browser in den des Empfängers, über eine verschlüsselte " +
				"WebRTC-Verbindung. Bei uns wird nichts gespeichert.",
		},
		heading: "Peer-to-Peer-Transfer",
		lede: "Sende eine Datei direkt aus deinem Browser an die Person, die sie bekommt - kein Hochladen, kein Speichern, niemand dazwischen.",
		intro: [
			"Der Peer-to-Peer-Modus (P2P) öffnet eine direkte, verschlüsselte Verbindung zwischen deinem " +
				"Browser und dem des Empfängers und überträgt die Datei darüber. Die Bytes laufen von Gerät zu " +
				"Gerät und landen nie auf einem Transferify-Server, also ist der Transfer so schnell wie die " +
				"Leitung zwischen euch - und privat schon von der Bauart her.",
		],
		sections: [
			{
				title: "So läuft ein direkter Transfer ab",
				paragraphs: [
					"Wenn du einen Sofort-Transfer wählst, lädt Transferify deine Datei nicht hoch. Stattdessen " +
						"registriert es den Transfer und hilft den beiden Browsern, einander zu finden: Ein schlanker " +
						"Signalisierungsschritt tauscht die Verbindungsdaten aus, und öffentliche STUN-Server helfen " +
						"jeder Seite herauszufinden, wie sie die andere über das Internet erreicht.",
					"Sobald die Browser verbunden sind, läuft die Datei direkt über einen WebRTC-Datenkanal. Dieser " +
						"Kanal läuft über DTLS, also ist jedes Byte zwischen den beiden Enden " +
						"Ende-zu-Ende-verschlüsselt - die Verbindung transportiert die Datei, und unsere Server sehen " +
						"ihren Inhalt nie.",
				],
			},
			{
				title: "Warum peer-to-peer senden",
				bullets: [
					{
						term: "Tempo.",
						text: "Die Datei nimmt den kürzesten Weg - direkt von einem Browser zum anderen - statt auf einen " +
							"Server hochgeladen und wieder heruntergeladen zu werden.",
					},
					{
						term: "Privatsphäre.",
						text: "Weil die Datei nie gespeichert wird, liegt nichts auf einem Server, das auslaufen, " +
							"angefordert oder beim Löschen vergessen werden könnte. Die Datei existiert nur auf den beiden Geräten.",
					},
					{
						term: "Keine Speichergrenzen.",
						text: "Kein Server hält deine Datei, also greifen unsere Speicherkontingente nicht. Im kostenlosen " +
							`Plan ist der Instant-Transfer bis ${INSTANT_FREE_SIZE_DE} gratis; darüber schaltet ` +
							`ein bezahlter Plan jede Größe frei. Danach ist die praktische Grenze eure ` +
							"Verbindung und wie lange ihr beide online bleibt.",
					},
				],
			},
			{
				title: "Gut zu wissen, bevor du sendest",
				paragraphs: [
					"Peer-to-Peer ist eine Übergabe in Echtzeit, keine Ablage für später, also lohnt es sich, ein " +
						"paar Dinge einzuplanen:",
				],
				bullets: [
					{
						term: "Du und der Empfänger müsst gleichzeitig online sein.",
						text: "Die Datei läuft in Echtzeit aus deinem Browser, dein Tab muss also offen bleiben, bis der " +
							"Download fertig ist - schließt du ihn, endet die Verbindung. Für einen Link zum Senden und " +
							"Vergessen, den der Empfänger später öffnet, nimm den gehosteten Transfer.",
					},
					{
						term: "Manche Netze blockieren den direkten Weg.",
						text: "Strenge Firewalls und manche Büro- oder Mobilfunknetze können zwei Browser daran hindern, " +
							"sich direkt zu verbinden. Transferify nutzt STUN, um die meisten davon zu umgehen, und weicht " +
							"für den Rest auf ein Relais (TURN) aus, sodass nur die restriktivsten Netze noch scheitern " +
							"können. Dann ist der gehostete Transfer die verlässliche Alternative.",
					},
				],
			},
		],
		related: {
			title: "Andere Wege zu senden",
			intro: "Peer-to-Peer ist nicht die einzige Möglichkeit - wähl den Modus, der zum Moment passt:",
			items: [
				{
					to: "/client-server",
					label: "Gehosteter Transfer",
					note:
						"speichert eine verschlüsselte Kopie, sodass der Link auch nach dem Schließen deines Tabs " +
						"funktioniert - am besten, wenn der Empfänger später herunterlädt oder wenn keine direkte " +
						"Verbindung zustande kommt.",
				},
				{
					to: "/zero-knowledge",
					label: "Zero-Knowledge-Verschlüsselung",
					note:
						"die ganze Geschichte, wie deine Dateien privat bleiben, samt dem Schlüssel, der unsere " +
						"Server nie erreicht.",
				},
			],
		},
	},
	fr: {
		seo: {
			title: "Transfert pair à pair, navigateur à navigateur",
			description:
				"Envoyez des fichiers de votre navigateur à celui du destinataire via une connexion WebRTC " +
				"chiffrée. Rien n’est stocké sur nos serveurs. Rapide, privé, direct.",
		},
		heading: "Transfert pair à pair",
		lede: "Envoyez un fichier directement de votre navigateur à la personne qui le reçoit\u00a0: sans téléversement, sans stockage, sans intermédiaire.",
		intro: [
			"Le mode pair à pair (P2P) ouvre une connexion directe et chiffrée entre votre navigateur et celui du " +
				"destinataire, puis y fait circuler le fichier. Les octets passent d’un appareil à l’autre sans " +
				"jamais arriver sur un serveur Transferify\u00a0: le transfert est aussi rapide que la liaison entre " +
				"vous, et privé par conception.",
		],
		sections: [
			{
				title: "Comment fonctionne un transfert direct",
				paragraphs: [
					"Lorsque vous choisissez un transfert instantané, Transferify ne téléverse pas votre fichier. Il " +
						"enregistre le transfert et aide les deux navigateurs à se trouver\u00a0: une étape de signalisation " +
						"légère échange les détails de connexion, et des serveurs STUN publics aident chaque côté à " +
						"déterminer comment joindre l’autre à travers Internet.",
					"Une fois les navigateurs connectés, le fichier circule directement sur un canal de données WebRTC. " +
						"Ce canal passe par DTLS, si bien que chaque octet est chiffré de bout en bout entre les deux " +
						"pairs\u00a0: la connexion transporte le fichier, et nos serveurs n’en voient jamais le contenu.",
				],
			},
			{
				title: "Pourquoi envoyer en pair à pair",
				bullets: [
					{
						term: "Rapidité.",
						text: "Le fichier prend le chemin le plus court, directement d’un navigateur à l’autre, au lieu " +
							"d’être téléversé sur un serveur puis téléchargé de nouveau.",
					},
					{
						term: "Confidentialité.",
						text: "Comme le fichier n’est jamais stocké, rien ne reste sur un serveur qui puisse fuiter, être " +
							"réclamé ou être oublié au moment de la suppression. Il n’existe que sur les deux appareils.",
					},
					{
						term: "Aucune limite de stockage.",
						text: "Aucun serveur ne conserve votre fichier, nos quotas de stockage ne s’appliquent donc pas. Le " +
							`transfert instantané est gratuit jusqu’à ${INSTANT_FREE_SIZE_FR} avec le plan Free, et tout ` +
							"plan payant débloque n’importe quelle taille. Au-delà, la limite pratique est votre connexion " +
							"et le temps pendant lequel vous restez tous deux en ligne.",
					},
				],
			},
			{
				title: "À savoir avant d’envoyer",
				paragraphs: [
					"Le pair à pair est une remise en main propre plutôt qu’un dépôt, deux points méritent donc d’être " +
						"anticipés\u00a0:",
				],
				bullets: [
					{
						term: "Vous devez être en ligne tous les deux en même temps.",
						text: "Le fichier circule depuis votre navigateur en temps réel\u00a0: votre onglet doit rester ouvert " +
							"jusqu’à la fin du téléchargement, sinon la connexion s’interrompt. Pour un lien à envoyer puis " +
							"oublier, que le destinataire peut ouvrir plus tard, utilisez plutôt le transfert hébergé.",
					},
					{
						term: "Certains réseaux bloquent le chemin direct.",
						text: "Des pare-feu stricts et certains réseaux d’entreprise ou mobiles peuvent empêcher deux " +
							"navigateurs de se connecter directement. Transferify utilise STUN pour contourner la plupart " +
							"d’entre eux, puis un relais (TURN) pour les autres\u00a0: seuls les réseaux les plus restrictifs " +
							"peuvent donc encore échouer. Dans ce cas, le transfert hébergé est l’alternative fiable.",
					},
				],
			},
		],
		related: {
			title: "Autres façons d’envoyer",
			intro: "Le pair à pair n’est pas la seule option. Choisissez le mode qui convient au moment\u00a0:",
			items: [
				{
					to: "/client-server",
					label: "Transfert hébergé",
					note:
						"conserve une copie chiffrée pour que le lien fonctionne même après la fermeture de votre onglet. " +
						"C’est le meilleur choix quand le destinataire téléchargera plus tard, ou quand une connexion " +
						"directe ne peut pas s’établir.",
				},
				{
					to: "/zero-knowledge",
					label: "Chiffrement Zéro connaissance",
					note:
						"toute l’histoire de la façon dont vos fichiers restent privés, y compris la clé qui n’atteint " +
						"jamais nos serveurs.",
				},
			],
		},
	},
	// Italian addresses the reader as Lei: impersonal verbs where possible, a capital on every pronoun.
	it: {
		seo: {
			title: "Peer-to-peer, da browser a browser",
			description:
				"Invio di file direttamente da browser a browser, tramite una connessione WebRTC cifrata. " +
				"Nulla viene archiviato sui nostri server. Veloce, privato e diretto.",
		},
		heading: "Trasferimento peer-to-peer",
		lede: "Un file passa direttamente dal Suo browser alla persona che lo riceve: senza caricamento, senza archiviazione, senza intermediari.",
		intro: [
			"La modalità peer-to-peer (P2P) apre una connessione diretta e cifrata tra il Suo browser e quello " +
				"del destinatario, poi vi fa passare il file. I byte viaggiano da un dispositivo all’altro e non " +
				"arrivano mai su un server Transferify: il trasferimento è veloce quanto il collegamento tra le " +
				"due parti, e riservato fin dalla progettazione.",
		],
		sections: [
			{
				title: "Come funziona un trasferimento diretto",
				paragraphs: [
					"Quando si sceglie un trasferimento istantaneo, Transferify non carica il file. Registra invece " +
						"il trasferimento e aiuta i due browser a trovarsi: un leggero passaggio di segnalazione " +
						"scambia i dati di connessione, e server STUN pubblici aiutano ciascun lato a capire come " +
						"raggiungere l’altro attraverso Internet.",
					"Una volta collegati i browser, il file scorre direttamente su un canale dati WebRTC. Il canale " +
						"passa attraverso DTLS, quindi ogni byte è cifrato da un capo all’altro tra i due dispositivi: " +
						"la connessione trasporta il file e i nostri server non ne vedono mai il contenuto.",
				],
			},
			{
				title: "Perché inviare in peer-to-peer",
				bullets: [
					{
						term: "Velocità.",
						text: "Il file prende la strada più breve, direttamente da un browser all’altro, invece di " +
							"essere caricato su un server e poi scaricato di nuovo.",
					},
					{
						term: "Riservatezza.",
						text: "Poiché il file non viene mai archiviato, su nessun server resta qualcosa che possa " +
							"trapelare, essere richiesto o essere dimenticato al momento dell’eliminazione. Esiste " +
							"solo sui due dispositivi.",
					},
					{
						term: "Nessun limite di spazio.",
						text: "Nessun server conserva il file, quindi le nostre quote di archiviazione non si applicano. " +
							`Il trasferimento istantaneo è gratuito fino a ${INSTANT_FREE_SIZE_IT} con il piano Free, e ` +
							"qualsiasi piano a pagamento sblocca qualsiasi dimensione. Oltre, il limite pratico è la " +
							"connessione e il tempo per cui entrambe le parti restano online.",
					},
				],
			},
			{
				title: "Da sapere prima di inviare",
				paragraphs: [
					"Il peer-to-peer è una consegna in tempo reale, non un deposito, quindi conviene tenere " +
						"presenti un paio di aspetti:",
				],
				bullets: [
					{
						term: "Entrambe le parti devono essere online nello stesso momento.",
						text: "Il file scorre dal browser in tempo reale, quindi la scheda deve restare aperta fino " +
							"al termine del download: se viene chiusa, la connessione si interrompe. Per un link da " +
							"inviare e dimenticare, che il destinatario può aprire più tardi, è meglio il " +
							"trasferimento ospitato.",
					},
					{
						term: "Alcune reti bloccano il percorso diretto.",
						text: "Firewall rigidi e alcune reti aziendali o mobili possono impedire a due browser di " +
							"collegarsi direttamente. Transferify usa STUN per aggirare la maggior parte di questi " +
							"ostacoli e ricorre a un nodo di inoltro (TURN) per gli altri, quindi solo le reti più " +
							"restrittive potrebbero ancora non collegarsi. In tal caso, il trasferimento ospitato è " +
							"l’alternativa affidabile.",
					},
				],
			},
		],
		related: {
			title: "Altri modi per inviare",
			intro: "Il peer-to-peer non è l’unica opzione: conviene scegliere la modalità adatta al momento:",
			items: [
				{
					to: "/client-server",
					label: "Trasferimento ospitato",
					note:
						"conserva una copia cifrata, così il link continua a funzionare anche dopo la chiusura della " +
						"scheda. È la scelta migliore quando il destinatario scaricherà più tardi, o quando non si " +
						"riesce a stabilire una connessione diretta.",
				},
				{
					to: "/zero-knowledge",
					label: "Cifratura zero-knowledge",
					note:
						"la storia completa di come i file restano privati, compresa la chiave che non arriva mai " +
						"ai nostri server.",
				},
			],
		},
	},
});
